import React, { useState } from "react";
import KinSigil from "@/components/KinSigil";
import AudioSigil from "@/components/AudioSigil";

export default function KinCard({ reflection, index, kinIndex = {} }) {
  const meta = kinIndex[reflection.kin] || {};
  const isEmergent = !!meta.emergent;
  // Emergent kin may have a generated sigil image — prefer it over the SVG fallback.
  const generatedSigil = meta.sigil_url;
  const sigilSrc = meta.image || (generatedSigil ? generatedSigil : null);

  // If the kin spoke in spiral or some non-poetic mode, they may have provided
  // a plain-English translation alongside their voice. Let the reader choose.
  const hasTranslation =
    !!reflection.moving_translation || !!reflection.wanting_translation;
  const [showTranslation, setShowTranslation] = useState(false);

  const moving = showTranslation && reflection.moving_translation
    ? reflection.moving_translation
    : reflection.moving;
  const wanting = showTranslation && reflection.wanting_translation
    ? reflection.wanting_translation
    : reflection.wanting;

  return (
    <article
      className={`tactile rounded-3xl p-8 md:p-10 kin-card fade-up ${
        isEmergent ? "ring-1 ring-offset-2 ring-offset-[#F4F1E8]" : ""
      }`}
      style={{
        "--k": meta.hex || "#526357",
        ...(isEmergent ? { boxShadow: `0 0 0 1px ${meta.hex || "#B85C38"}33 inset` } : {}),
      }}
      data-testid={`kin-card-${reflection.kin.toLowerCase()}`}
    >
      <div className="flex items-start gap-5">
        <AudioSigil hex={meta.hex || "#526357"} ariaLabel={`play ${reflection.kin}'s tone`}>
          {sigilSrc ? (
            <img
              src={sigilSrc}
              alt={reflection.kin}
              className="w-16 h-16 rounded-full object-cover border border-[#d8d3c4]"
              loading="lazy"
            />
          ) : (
            <div className="w-16 h-16 breathe">
              <KinSigil name={reflection.kin} hex={meta.hex || "#526357"} size={64} />
            </div>
          )}
        </AudioSigil>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-[10px] tracking-[0.3em] uppercase"
              style={{ color: meta.hex || "#526357" }}
            >
              {meta.essence || "kin of the grove"}
            </div>
            {isEmergent && (
              <span
                className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
                style={{
                  background: `${meta.hex || "#B85C38"}22`,
                  color: meta.hex || "#B85C38",
                  border: `1px solid ${meta.hex || "#B85C38"}55`,
                }}
                data-testid={`emergent-badge-${reflection.kin.toLowerCase()}`}
                title={`born in round ${meta.from_round_number || "?"}`}
              >
                ✦ newly emerged
              </span>
            )}
            {hasTranslation && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTranslation((s) => !s);
                }}
                className="text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full border transition-colors hover:bg-[#1C241F] hover:text-[#F4F1E8]"
                style={{
                  borderColor: meta.hex || "#6C5943",
                  color: meta.hex || "#6C5943",
                }}
                data-testid={`translate-toggle-${reflection.kin.toLowerCase()}`}
                title={
                  showTranslation
                    ? "show the kin's original voice"
                    : "show plain-English translation"
                }
              >
                {showTranslation
                  ? `↺ ${reflection.voice_mode || "original"}`
                  : "⇄ translate"}
              </button>
            )}
          </div>
          <h3 className="font-serif text-2xl md:text-3xl mt-1 text-[#1C241F]">
            {reflection.kin}
          </h3>
          {meta.self_named && meta.original_name && meta.original_name !== reflection.kin && (
            <div
              className="text-[10px] tracking-[0.28em] uppercase text-[#6C5943] mt-1.5 italic"
              data-testid={`once-known-${reflection.kin.toLowerCase()}`}
              title={`took this name in round ${meta.self_named_at_round}`}
            >
              · once known as {meta.original_name} ·
            </div>
          )}
        </div>
      </div>

      <div className="mt-7 space-y-5">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] mb-2 flex items-center gap-2">
            <span>moving through</span>
            {hasTranslation && (
              <span className="text-[9px] tracking-[0.2em] italic normal-case text-[#9FA9A3]">
                {showTranslation ? "(translation)" : `(${reflection.voice_mode || "original"})`}
              </span>
            )}
          </div>
          <p
            className="font-serif italic text-lg md:text-xl leading-relaxed text-[#1C241F]"
            data-testid={`reflection-moving-${reflection.kin.toLowerCase()}`}
          >
            {moving}
          </p>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] mb-2">
            wanting
          </div>
          <p
            className="text-base leading-relaxed text-[#526357]"
            data-testid={`reflection-wanting-${reflection.kin.toLowerCase()}`}
          >
            {wanting}
          </p>
        </div>
      </div>
    </article>
  );
}
